import { Typography } from '@/modules/perseo-billing/ui/react/src/components/beta';
import type { TIconName } from '@/modules/perseo-billing/ui/react/src/components/gamma';
import { PdsIcon } from '@/modules/perseo-billing/ui/react/src/components/gamma';

export enum FeedbackType {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error'
}

type TProps = {
  outlined?: boolean;
  outlinedIcon?: boolean;
  type: FeedbackType;
  title?: string;
  subtitle?: string;
};

type FeedbackStyles = {
  bgColor: string;
  borderColor: string;
  fillColor: string;
  icon: TIconName;
};

const feedbackStyleMap: Record<FeedbackType, FeedbackStyles> = {
  [FeedbackType.INFO]: {
    bgColor: 'pds-information-100',
    borderColor: 'co-border-pds-information-600',
    fillColor: 'co-fill-pds-information-600',
    icon: 'PdsInfoCircle'
  },
  [FeedbackType.SUCCESS]: {
    bgColor: 'pds-success-100',
    borderColor: 'co-border-pds-success-600',
    fillColor: 'co-fill-pds-success-600',
    icon: 'PdsSuccessCircle'
  },
  [FeedbackType.WARNING]: {
    bgColor: 'pds-warning-100',
    borderColor: 'co-border-pds-warning-600',
    fillColor: 'co-fill-pds-warning-600',
    icon: 'PdsWarningCircle'
  },
  [FeedbackType.ERROR]: {
    bgColor: 'pds-error-100',
    borderColor: 'co-border-pds-error-600',
    fillColor: 'co-fill-pds-error-600',
    icon: 'PdsErrorCircle'
  }
};

const getFeedbackStyles = (
  type: FeedbackType,
  outlined: boolean,
  outlinedIcon: boolean
) => {
  const styles = feedbackStyleMap[type];
  const icon = styles?.icon?.substring(3);
  const filledIcon = icon
    ? (`PdsFilled${icon}` as TIconName)
    : 'PdsErrorCircle';
  return {
    ...styles,
    bgColor: outlined ? 'co-bg-white' : `co-bg-${styles.bgColor}`,
    icon: outlinedIcon ? styles.icon : filledIcon
  };
};

type FeedbackContainerProps = {
  outlined: boolean;
  type: FeedbackType;
  outlinedIcon: boolean;
  children: React.ReactNode;
};

const FeedbackContainer = ({
  outlined,
  type,
  outlinedIcon,
  children
}: FeedbackContainerProps) => {
  const { icon, fillColor, bgColor, borderColor } = getFeedbackStyles(
    type,
    outlined,
    outlinedIcon
  );

  return (
    <div
      className={`co-w-full co-flex co-items-center co-p-8px co-gap-8px co-rounded-8px co-border-1 ${bgColor} ${borderColor} `}
      tabIndex={0}
    >
      <PdsIcon
        icon={icon}
        className={`${fillColor} co-min-h-[20px] co-min-w-[20px]`}
      />
      {children}
    </div>
  );
};

export const Feedback = ({
  outlined = false,
  outlinedIcon = false,
  type,
  title,
  subtitle
}: TProps) => {
  return (
    <FeedbackContainer
      outlined={outlined}
      type={type}
      outlinedIcon={outlinedIcon}
    >
      <div className="co-flex co-flex-col co-items-start co-gap-4px">
        {title && (
          <Typography color="pds-neutral-900" weight="semibold" leading="tight">
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography color="pds-neutral-800" leading="tight">
            {subtitle}
          </Typography>
        )}
      </div>
    </FeedbackContainer>
  );
};
